// Catches a crash while rendering a screen.
//
// Without this, any render error unmounts the whole app and leaves a blank
// white page with no message — which is how an error ends up reported as
// "something is broken, I don't know where". The crash is now shown as a
// readable message with a way out, and reported to the server.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/errorReporter';

interface Props {
  children: ReactNode;
  /** Changes when the route changes, so moving to another page clears the error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError('render', error, { componentStack: info.componentStack?.slice(0, 3000) });
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg border border-danger/30 bg-surface p-6 text-center space-y-3">
          <p className="text-subhead font-display font-semibold text-ink">Halaman ini mengalami error</p>
          <p className="text-small text-ink-subtle">
            Error sudah dicatat otomatis untuk diperiksa. Muat ulang halaman — data yang sudah tersimpan tidak hilang.
          </p>
          <p className="text-caption font-mono text-danger break-words">{this.state.error.message}</p>
          <div className="flex justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded bg-primary px-4 py-2 text-small font-medium text-white hover:bg-primary/90"
            >
              Muat ulang
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/dashboard'; }}
              className="rounded border border-line px-4 py-2 text-small text-ink hover:bg-canvas"
            >
              Ke Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
